import type { QueryCache } from '@tanstack/react-query';
import { InlineQueryError } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import { transactionsPageOf } from './dealstackAiQueries.utils';
import type { PageQuery, TransactionPageView } from './useTransactionPages.types';

/** Later pages that went stale off screen, with the data they went stale with (a refetch replaces it). */
const placeholderSince = new WeakMap<PageQuery, number>();

/**
 * How a later page shows. A page a write made stale shows placeholder rows until it has refetched; one that was on
 * screen at the write keeps its rows while its refetch runs, and keeps them if that refetch fails. A page that was a
 * placeholder stays one while its refetch runs (it came back into view, or "Try again"), and shows as failed if that
 * refetch fails (query-core marks a failed fetch invalidated and idle, so a failure is told apart by `status`).
 */
export const pageView = (query: PageQuery): TransactionPageView => {
	const { status, fetchStatus, isInvalidated, dataUpdatedAt } = query.state;
	const wasPlaceholder = placeholderSince.get(query) === dataUpdatedAt;
	if (fetchStatus !== 'idle') {
		return wasPlaceholder ? 'placeholder' : 'rows';
	}
	if (status === 'error') {
		return wasPlaceholder ? 'failed' : 'rows';
	}
	return isInvalidated ? 'placeholder' : 'rows';
};

/** Remembers a stale later page coming back into view as a placeholder, before its refetch starts. */
export const watchPlaceholderPages = (queryCache: QueryCache) =>
	queryCache.subscribe((event) => {
		const page = transactionsPageOf(event.query.queryKey);
		if (
			event.type === 'observerAdded' &&
			page !== null &&
			page > 1 &&
			event.query.state.isInvalidated &&
			event.query.state.fetchStatus === 'idle'
		) {
			placeholderSince.set(event.query, event.query.state.dataUpdatedAt);
		}
	});

/**
 * A later page's failure shows in the table, so it raises no error toast: one that never loaded (the footer's
 * "Couldn't load more rows · Try again"), or one that was a placeholder (the same, on its places). A page whose
 * rows were on screen keeps them when its refetch fails, and that failure toasts.
 */
export const TRANSACTIONS_PAGE_INLINE_ERROR = new InlineQueryError(
	(query) => query.state.data === undefined || pageView(query) === 'failed'
);
